import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  FormGroup,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import axiosInstance from '../../../utils/axios';
import { APIS } from '../../../utils/serviceUrls';
import { Event } from '../../../@types/event';
import { FormProvider, RHFTextField, RHFSwitch, RHFSelect } from '../../../components/hook-form';
import ProgramBatchesField from './ProgramBatchesField';
import { UploadAvatar, UploadSingleFile } from '../../../components/upload';
import Iconify from '../../../components/Iconify';
import TextMaxLine from '../../../components/TextMaxLine';
import { EventSchema } from './index';
import LectureQuizBuilder from './LectureQuizBuilder';
import LectureAssignmentBuilder from './LectureAssignmentBuilder';
import RHFOrderField from './RHFOrderField';
import {
  DIRTY_ORDER_TEXT,
  FieldError,
  RELOAD_DROPS_TEXT,
  cleanQuizForSave,
  differsFrom,
  invalidLines,
  nextLectureOrder,
  pathLabel,
  programSaveErrors,
  swapIds,
} from './eventLectures';
import { zoomMessages } from '../../dashboard/zoom/zoomLabels';

type EventNewEditFormProps = {
  isEdit: boolean;
  currentEvent?: Event;
  // `saved` = the program as the API stored it (the list row is replaced by it).
  handleClose(saved?: Event): void;
  // R4-E7: unsaved changes or an upload in flight, for the modal's close guard.
  onDirtyChange?: (dirty: boolean) => void;
};

type FormAlert = { lines: string[]; conflict: boolean };
const NO_ALERT: FormAlert = { lines: [], conflict: false };
// The banner image has no lecture: its uploads are counted under this key.
const BANNER_SLOT = '__banner__';

const emptyLecture = {
  title: '',
  title_ar: '',
  info: '',
  info_ar: '',
  speaker: '',
  speaker_ar: '',
  thumbnail: '',
  htmlUrl: '',
  htmlUrl_ar: '',
  labFile: '',
  recordingUrl: '',
  order: 0,
  status: 'published' as const,
  // Batch ids this lecture is hidden for (shows as "opening soon"). Set per
  // batch in the Batches editor.
  excludedBatches: [] as string[],
  quiz: {
    title: '',
    title_ar: '',
    duration: 0,
    attemptsLimit: 3,
    passingScore: 50,
    secondsPerQuestion: 0,
    questionsToServe: 0,
    active: true,
    excludedBatches: [],
    showAnswers: false,
    questions: [],
  },
};

const EventNewEditForm = ({
  isEdit,
  currentEvent,
  handleClose,
  onDirtyChange,
}: EventNewEditFormProps) => {
  // The program as last read from the API (fresh by id when the editor opened).
  // A lecture move or a "Reload program" replaces it, and the form is reset to
  // it — never to a list snapshot (R4-E1).
  const [event, setEvent] = useState<Event | undefined>(currentEvent);
  const { defaultValues: baseDefaults, NewEventSchema } = EventSchema(event);

  // The shared batch defaults leave `instructor` out, so the per-cohort
  // Instructor select opened on "None" and the value was never sent. Carry the
  // stored teacher in here (as a plain id string). A cohort without one stays
  // `undefined`, which the save omits, so the API keeps whatever it stores.
  const defaultValues = useMemo(
    () => ({
      ...baseDefaults,
      batches: ((baseDefaults as any).batches || []).map((b: any, i: number) => {
        const stored = (event?.batches?.[i] as any)?.instructor;
        return stored ? { ...b, instructor: String(stored?._id ?? stored) } : b;
      }),
    }),
    [baseDefaults, event]
  );
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm<any>({
    resolver: yupResolver(NewEventSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    control,
    setValue,
    setError,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const { fields, append, remove, move } = useFieldArray({ control, name: 'lectures' });

  const values = watch();

  // The loaded program changed (a lecture move saved, or "Reload program"):
  // the form shows it. Skipped on the first render — useForm already has it.
  const firstDefaults = useRef(true);
  useEffect(() => {
    if (firstDefaults.current) {
      firstDefaults.current = false;
      return;
    }
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Pending uploads per lecture (by field id) and for the banner. Save, the
  // arrows and closing wait for all of them; a lecture cannot be deleted while
  // its own file is uploading (R4-E6).
  const [uploads, setUploads] = useState<Record<string, number>>({});
  const pendingUploads = Object.values(uploads).reduce((sum, n) => sum + n, 0);
  const [moving, setMoving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [formAlert, setFormAlert] = useState<FormAlert>(NO_ALERT);
  const [lectureAlert, setLectureAlert] = useState<FormAlert>(NO_ALERT);
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  // Compared with what the editor loaded, not react-hook-form's isDirty: the
  // batches editor writes with setValue() and no shouldDirty, so isDirty would
  // miss those edits and a move or a reload would drop them without asking.
  const dirty = differsFrom(values, defaultValues);
  useEffect(() => {
    onDirtyChange?.(dirty || pendingUploads > 0);
  }, [dirty, pendingUploads, onDirtyChange]);

  // Direct (pre-batch) assignments that still exist on this program. The
  // picker that created them is gone — students are enrolled through batches —
  // but the value stays in form state so saving does not silently wipe it.
  const legacyAssigned = ((values?.assignedUsers as string[]) || []).map(String);
  const legacyAssignedCount = legacyAssigned.length;

  // How many of those legacy students would lose ALL access if the array were
  // cleared — i.e. they are in no batch. Drives whether clearing is offered.
  const batchStudentIds = new Set(
    ((values?.batches as any[]) || []).flatMap((b: any) =>
      ((b?.students as any[]) || []).map((s: any) => String(s)),
    ),
  );
  const legacyNotInAnyBatch = legacyAssigned.filter((id) => !batchStudentIds.has(id)).length;

  // A program nobody can see: no batch students and no legacy direct grants.
  // Access fails CLOSED (an empty audience means nobody, despite what the old
  // schema comment claimed), so this is worth saying out loud before saving.
  const audienceIsEmpty = batchStudentIds.size === 0 && legacyAssignedCount === 0;

  const uploadImage = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
    return response.data;
  };

  const uploadHtml = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.HTML, form);
    return response.data;
  };

  const uploadAny = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.ANY, form);
    return response.data;
  };

  /**
   * One upload into one field. The lecture is found by its field id when the
   * file has ARRIVED, not by the index it had when it was dropped — a lecture
   * removed or moved meanwhile would otherwise receive another lecture's file.
   * `fieldId` null = a program field (the banner).
   */
  const uploadInto = async (
    fieldId: string | null,
    key: string,
    file: File | undefined,
    upload: (f: File) => Promise<any>
  ) => {
    if (!file) return;
    const slot = fieldId || BANNER_SLOT;
    setUploads((u) => ({ ...u, [slot]: (u[slot] || 0) + 1 }));
    try {
      const res = await upload(file);
      if (!res?.url) throw new Error('the server returned no file address');
      let path = key;
      if (fieldId) {
        const at = fieldsRef.current.findIndex((f) => f.id === fieldId);
        if (at < 0) {
          enqueueSnackbar(
            'The lecture was removed before the upload finished; the file was not attached.',
            { variant: 'warning' }
          );
          return;
        }
        path = `lectures.${at}.${key}`;
      }
      setValue(path, res.url, { shouldDirty: true });
    } catch (e: any) {
      const reason = e instanceof Error ? e.message : zoomMessages(e, 'unknown reason')[0];
      enqueueSnackbar(`Upload failed: ${reason}`, { variant: 'error' });
    } finally {
      setUploads((u) => ({ ...u, [slot]: Math.max(0, (u[slot] || 0) - 1) }));
    }
  };

  // Puts a failed save's answer on the screen: each `path: text` from the API
  // on its field, every line in the Alert at the top.
  const showSaveError = (err: any, fallback: string, where: 'form' | 'lectures') => {
    const info = programSaveErrors(err, fallback);
    info.fieldErrors.forEach((f) => setError(f.path, { type: 'server', message: f.message }));
    (where === 'form' ? setFormAlert : setLectureAlert)({
      lines: info.lines,
      conflict: info.conflict,
    });
  };

  // "Reload program": the latest stored version, read by id. Unsaved edits
  // here are dropped (the Alert says so before the click).
  const reloadProgram = async () => {
    if (!event?._id) return;
    setReloading(true);
    try {
      const res = await axiosInstance.get(APIS.EVENTS.BY_ID(event._id));
      setEvent((res.data?.data ?? res.data) as Event);
      setFormAlert(NO_ALERT);
      setLectureAlert(NO_ALERT);
    } catch (e: any) {
      showSaveError(e, 'Could not reload this program.', 'form');
    } finally {
      setReloading(false);
    }
  };

  // ↑/↓ on a saved lecture (R4-E3): the move shows at once and is saved by the
  // server's reorder (every lecture id + the loaded version). Success → the
  // form shows the program the server returned (new version); failure → the
  // loaded program again, with the reason above the list. Only offered while
  // nothing else is unsaved, so neither outcome can drop an edit.
  const moveLecture = async (index: number, dir: -1 | 1) => {
    if (!event?._id || dirty || pendingUploads > 0 || moving) return;
    const ids = swapIds(values.lectures, index, dir);
    if (!ids) return;
    setMoving(true);
    setLectureAlert(NO_ALERT);
    move(index, index + dir);
    try {
      const res = await axiosInstance.put(APIS.EVENTS.LECTURE_ORDER(event._id), {
        ids,
        expectedUpdatedAt: event.updatedAt ?? '',
      });
      setEvent((res.data?.data ?? res.data) as Event);
    } catch (e: any) {
      reset(defaultValues);
      showSaveError(e, 'Could not save the new order. Nothing was changed.', 'lectures');
    } finally {
      setMoving(false);
    }
  };

  // Client rules failed. An error on a field that has an input shows under it;
  // one with no input on screen (a legacy lecture order, quiz internals) would
  // block Save without a word, so every such line goes to the top Alert.
  const onInvalid = (errs: any) => {
    const lines = invalidLines(errs);
    setFormAlert({
      lines: lines.length ? lines : ['Some fields need attention — they are marked in red below.'],
      conflict: false,
    });
  };

  const onSubmit = async (data: any) => {
    setFormAlert(NO_ALERT);
    if (pendingUploads > 0 || moving) return;
    // Quiz clean-up: only TRAILING blank answers and questions are dropped
    // (attempts are graded by question index); a blank in the middle is a
    // field error and nothing is sent.
    if (Array.isArray(data.lectures)) {
      const quizErrors: FieldError[] = [];
      data.lectures = data.lectures.map((l: any, i: number) => {
        const out = cleanQuizForSave(l?.quiz, `lectures.${i}.quiz`);
        quizErrors.push(...out.errors);
        return out.quiz === l?.quiz ? l : { ...l, quiz: out.quiz };
      });
      if (quizErrors.length) {
        quizErrors.forEach((e) => setError(e.path, { type: 'manual', message: e.message }));
        setFormAlert({
          lines: quizErrors.map((e) => `${pathLabel(e.path)}: ${e.message}`),
          conflict: false,
        });
        return;
      }
    }
    if (data.date) {
      data.date = new Date(data.date).toISOString();
    } else {
      delete data?.date;
    }
    // Normalise batches for the API: the DTO validates dates as ISO strings and
    // capacity as an int, so blank inputs must be dropped rather than sent as ''.
    // `_id` is preserved for existing batches so their stamped quiz history stays
    // attached (a new id would orphan it).
    if (Array.isArray(data.batches)) {
      data.batches = data.batches
        .filter((b: any) => (b?.name || '').trim())
        .map((b: any) => {
          const out: any = {
            name: String(b.name).trim(),
            active: b.active !== false,
            students: (b.students || []).map((s: any) => String(s)),
          };
          if (b._id) out._id = String(b._id);
          if ((b.name_ar || '').trim()) out.name_ar = String(b.name_ar).trim();
          if (b.startDate) out.startDate = new Date(b.startDate).toISOString();
          if (b.endDate) out.endDate = new Date(b.endDate).toISOString();
          const cap = Number(b.capacity);
          if (b.capacity !== '' && b.capacity !== null && Number.isFinite(cap)) {
            out.capacity = cap;
          }
          // Who runs the cohort: the instructor portal and grading scope on it.
          // A chosen teacher is sent as its id; "None" ('' from the select) is
          // sent as null, which the API treats as a deliberate removal; an
          // untouched unknown value is omitted so the API keeps what it has.
          if (b.instructor === '' || b.instructor === null) out.instructor = null;
          else if (b.instructor) out.instructor = String(b.instructor?._id ?? b.instructor);
          return out;
        });
    }
    // The version this editor loaded: a save in between is a 409, never a
    // silent overwrite of someone else's links (R4-E1). '' = a program stored
    // before versions existed (the API matches a missing updatedAt).
    if (event?._id) data.expectedUpdatedAt = event.updatedAt ?? '';
    try {
      const res = event?._id
        ? await axiosInstance.patch(APIS.EVENTS.PATCH(event._id), data)
        : await axiosInstance.post(APIS.EVENTS.ADD, data);
      const saved = (res?.data?.data ?? res?.data) as Event | undefined;
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose(saved && saved._id ? saved : undefined);
    } catch (err) {
      showSaveError(err, !isEdit ? 'Unable to create Program!' : 'Unable to edit Program', 'form');
    }
  };

  // Why an arrow is off ('' = it works).
  const arrowBlocker = (lectureId?: string): string => {
    if (moving) return 'Saving the order…';
    if (pendingUploads > 0) return 'Wait for the upload to finish.';
    if (dirty) return DIRTY_ORDER_TEXT;
    if (!lectureId) return 'Save the program once to move this lecture.';
    return '';
  };

  const renderAlert = (alert: FormAlert, onClose: () => void) =>
    alert.lines.length > 0 && (
      <Alert
        severity={alert.conflict ? 'warning' : 'error'}
        onClose={alert.conflict ? undefined : onClose}
        action={
          alert.conflict && event?._id ? (
            <Button color="inherit" size="small" disabled={reloading} onClick={reloadProgram}>
              {reloading ? 'Reloading…' : 'Reload program'}
            </Button>
          ) : undefined
        }
      >
        {alert.lines.map((line, i) => (
          <Typography key={i} variant="body2">
            {line}
          </Typography>
        ))}
        {alert.conflict && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            {RELOAD_DROPS_TEXT}
          </Typography>
        )}
      </Alert>
    );

  return (
    <Stack height={'100%'} style={{ overflow: 'auto' }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <TextMaxLine marginBottom={'0px'} marginTop={'0px'} fontSize={'22px'}>
          {isEdit ? ' Edit Program' : ' Create Program'}
        </TextMaxLine>
        <Stack direction={'column'} justifyContent={'space-between'} height={'auto'}>
          <Stack spacing={3} mt={2}>
            {renderAlert(formAlert, () => setFormAlert(NO_ALERT))}
            {/* Banner image */}
            <Stack alignItems={'center'} spacing={1}>
              <UploadAvatar
                accept={{ 'image/*': [] }}
                file={values.bannerImage}
                label={values.bannerImage ? 'Update banner' : 'Upload banner'}
                onDrop={(accepted: File[]) =>
                  uploadInto(null, 'bannerImage', accepted?.[0], uploadImage)
                }
              />
              <Typography variant="caption" color={'text.secondary'}>
                Banner image
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField
                name="slug"
                label="Slug"
                helperText="url path, e.g. ain-shams"
              />
              <RHFOrderField name="order" label="Order" />
              <RHFTextField name="title" label="Title" />
              <RHFTextField name="title_ar" label="Arabic Title" />
              <RHFTextField name="subtitle" label="Subtitle" />
              <RHFTextField name="subtitle_ar" label="Arabic Subtitle" />
              <RHFTextField name="location" label="Location" />
              <RHFTextField name="location_ar" label="Arabic Location" />
              <RHFTextField
                name="date"
                label="Date"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
              <RHFTextField name="ctaText" label="CTA Text" />
              <RHFTextField name="ctaText_ar" label="Arabic CTA Text" />
            </Box>

            <FormGroup sx={{ display: 'grid', columnGap: 1, rowGap: 1 }}>
              <RHFTextField
                multiline
                minRows={3}
                maxRows={8}
                name="description"
                label="Description"
              />
              <RHFTextField
                multiline
                minRows={3}
                maxRows={8}
                name="description_ar"
                label="Arabic Description"
              />
            </FormGroup>

            <RHFSwitch name="active" label="Active" />

            {/* Students are assigned through BATCHES only. The old
                "Assign to users" picker created a second, competing way to
                enrol people: an admin had to assign everyone here AND then
                place them in a batch, and anyone assigned only here had no
                batch — so they were excluded from per-batch quiz analysis and
                from per-batch availability rules. The field is kept in form
                state (and therefore round-trips untouched on save) so existing
                direct assignments are preserved, but it is no longer editable
                here. */}
            <Alert severity="info" sx={{ mt: 1 }}>
              Students are enrolled through <strong>Batches</strong> below — add a batch and pick
              its students there.
              {audienceIsEmpty && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  <strong>Nobody can see this program yet.</strong> It has no batch students, so it
                  is hidden from every learner until you add some.
                </Typography>
              )}
              {legacyAssignedCount > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    This program still has <strong>{legacyAssignedCount}</strong> student(s) assigned
                    directly, from before batches existed. They keep access, but they belong to no
                    batch — so they are missing from per-batch reports, and per-batch quiz rules do
                    not apply to them.
                  </Typography>

                  {/* Clearing is a REVOCATION, not tidy-up: for anyone not also
                      in a batch, assignedUsers is their ONLY route to the
                      program. Only offer the button once everyone has been
                      moved into a batch; until then, say exactly how many would
                      lose access. */}
                  {legacyNotInAnyBatch > 0 ? (
                    <Typography variant="body2" color="error.main">
                      <strong>{legacyNotInAnyBatch}</strong> of them are in no batch. Clearing now
                      would remove their access entirely — add them to a batch first, then the
                      clear option appears.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        All of them are also in a batch, so clearing is safe — batch membership
                        keeps their access.
                      </Typography>
                      <Button
                        size="small"
                        color="warning"
                        variant="outlined"
                        onClick={() => setValue('assignedUsers', [])}
                      >
                        Clear direct assignments
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Alert>

            {/* Batches (cohorts) — membership + per-batch quiz analysis */}
            <Divider />
            <ProgramBatchesField />

            {/* Lectures editor */}
            <Divider />
            <Stack
              direction={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <TextMaxLine sx={{ fontSize: '16px', fontWeight: '700' }}>Lectures</TextMaxLine>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                disabled={moving}
                // Last on the website too: one past the highest order (R4-E5).
                onClick={() =>
                  append({ ...emptyLecture, order: nextLectureOrder(values.lectures) })
                }
              >
                Add Lecture
              </Button>
            </Stack>

            {/* Lectures are listed in the website's order. The arrows save the
                new order at once through the server; they wait for the rest of
                the form to be saved first, so a move never drops an edit. */}
            {isEdit && fields.length > 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
                {dirty && !moving
                  ? DIRTY_ORDER_TEXT
                  : 'Use the arrows to change the order learners see. Each move is saved at once.'}
              </Typography>
            )}
            {renderAlert(lectureAlert, () => setLectureAlert(NO_ALERT))}

            {fields.map((field, index) => {
              const lecture = values.lectures?.[index] || {};
              const blocker = arrowBlocker(lecture?._id);
              const ownUpload = (uploads[field.id] || 0) > 0;
              return (
                <Card key={field.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    mb={1}
                  >
                    <Typography variant="subtitle2">
                      Lecture {index + 1} · position {index + 1} on the program page
                    </Typography>
                    <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
                      {isEdit && (
                        <Tooltip title={blocker}>
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Move up"
                              disabled={!!blocker || index === 0}
                              onClick={() => moveLecture(index, -1)}
                            >
                              <Iconify icon={'eva:arrow-up-fill'} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {isEdit && (
                        <Tooltip title={blocker}>
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Move down"
                              disabled={!!blocker || index === fields.length - 1}
                              onClick={() => moveLecture(index, 1)}
                            >
                              <Iconify icon={'eva:arrow-down-fill'} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title={ownUpload ? 'Wait for this lecture’s upload to finish.' : ''}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Delete lecture"
                            sx={{ color: 'error.main' }}
                            disabled={ownUpload || moving}
                            onClick={() => remove(index)}
                          >
                            <Iconify icon={'eva:trash-2-outline'} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      columnGap: 1,
                      rowGap: 1,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    <RHFTextField name={`lectures.${index}.title`} label="Title" />
                    <RHFTextField name={`lectures.${index}.title_ar`} label="Arabic Title" />
                    <RHFTextField name={`lectures.${index}.speaker`} label="Speaker" />
                    <RHFTextField
                      name={`lectures.${index}.speaker_ar`}
                      label="Arabic Speaker"
                    />
                    <RHFSelect name={`lectures.${index}.status`} label="Status">
                      <option value="published">Published</option>
                      <option value="soon">Opening soon</option>
                    </RHFSelect>
                  </Box>

                  <FormGroup sx={{ display: 'grid', columnGap: 1, rowGap: 1, mt: 1 }}>
                    <RHFTextField
                      multiline
                      minRows={2}
                      maxRows={5}
                      name={`lectures.${index}.info`}
                      label="Info"
                    />
                    <RHFTextField
                      multiline
                      minRows={2}
                      maxRows={5}
                      name={`lectures.${index}.info_ar`}
                      label="Arabic Info"
                    />
                  </FormGroup>

                  <Box
                    sx={{
                      display: 'grid',
                      columnGap: 2,
                      rowGap: 1,
                      mt: 2,
                      gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    {/* Lecture thumbnail */}
                    <Stack spacing={1}>
                      <Typography variant="caption" fontWeight={700}>
                        Thumbnail
                      </Typography>
                      <UploadSingleFile
                        accept={{ 'image/*': [] }}
                        file={lecture.thumbnail}
                        label={'Select or drop image'}
                        onDropAccepted={(accepted: File[]) =>
                          uploadInto(field.id, 'thumbnail', accepted?.[0], uploadImage)
                        }
                      />
                      {lecture.thumbnail && (
                        <Link href={lecture.thumbnail} target="_blank" rel="noopener" variant="caption">
                          View thumbnail
                        </Link>
                      )}
                    </Stack>

                    {/* Lecture HTML file */}
                    <Stack spacing={1}>
                      <Typography variant="caption" fontWeight={700}>
                        Lecture HTML
                      </Typography>
                      <UploadSingleFile
                        accept={{ 'text/html': ['.html', '.htm'] }}
                        file={lecture.htmlUrl}
                        label={'Select or drop .html file'}
                        onDropAccepted={(accepted: File[]) =>
                          uploadInto(field.id, 'htmlUrl', accepted?.[0], uploadHtml)
                        }
                      />
                      {lecture.htmlUrl ? (
                        <Link href={lecture.htmlUrl} target="_blank" rel="noopener" variant="caption">
                          Open current HTML
                        </Link>
                      ) : (
                        <Typography variant="caption" color={'text.disabled'}>
                          no HTML yet
                        </Typography>
                      )}

                      {/* Arabic lecture HTML (optional) — when present, the site shows an
                          EN/AR toggle and serves this file for Arabic viewers. */}
                      <Typography variant="caption" fontWeight={700} sx={{ mt: 1 }}>
                        Lecture HTML — Arabic (optional)
                      </Typography>
                      <UploadSingleFile
                        accept={{ 'text/html': ['.html', '.htm'] }}
                        file={lecture.htmlUrl_ar}
                        label={'Select or drop the Arabic .html file'}
                        onDropAccepted={(accepted: File[]) =>
                          uploadInto(field.id, 'htmlUrl_ar', accepted?.[0], uploadHtml)
                        }
                      />
                      {lecture.htmlUrl_ar ? (
                        <Link href={lecture.htmlUrl_ar} target="_blank" rel="noopener" variant="caption">
                          Open current Arabic HTML
                        </Link>
                      ) : (
                        <Typography variant="caption" color={'text.disabled'}>
                          no Arabic HTML yet
                        </Typography>
                      )}

                      <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        Lab file (download)
                      </Typography>
                      <UploadSingleFile
                        accept={{ 'application/octet-stream': ['.ipynb', '.zip', '.pdf'] }}
                        file={lecture.labFile}
                        label={'Select or drop lab file (.ipynb, .zip, .pdf)'}
                        onDropAccepted={(accepted: File[]) =>
                          uploadInto(field.id, 'labFile', accepted?.[0], uploadAny)
                        }
                      />
                      {lecture.labFile ? (
                        <Link href={lecture.labFile} target="_blank" rel="noopener" variant="caption">
                          Current lab file
                        </Link>
                      ) : (
                        <Typography variant="caption" color={'text.disabled'}>
                          no lab file yet
                        </Typography>
                      )}

                      <Typography variant="subtitle2" sx={{ mt: 2 }}>
                        Session recording (link)
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="https://drive.google.com/... or OneDrive / YouTube link"
                        helperText="Paste the link to this session's recording. Students get a 'Recording' button that opens it. Leave empty to hide the button."
                        value={lecture.recordingUrl || ''}
                        onChange={(e) =>
                          setValue(`lectures.${index}.recordingUrl`, e.target.value, {
                            shouldDirty: true,
                          })
                        }
                      />
                    </Stack>
                  </Box>

                  <LectureQuizBuilder lectureIndex={index} />

                  <LectureAssignmentBuilder lectureIndex={index} />
                </Card>
              );
            })}

            {fields.length === 0 && (
              <Typography variant="body2" color={'text.disabled'}>
                No lectures yet. Add one to get started.
              </Typography>
            )}
          </Stack>

          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
              // Save waits for every upload (a file still on its way would be
              // missing from the save) and for a lecture move being saved.
              disabled={pendingUploads > 0 || moving}
            >
              {pendingUploads > 0 ? 'Uploading…' : !isEdit ? 'Create Program' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
};

export default EventNewEditForm;
